import ReactDOMServer from "react-dom/server";

import {
    combineConfigs,
    gradientConfig,
    noMarkerConfig,
    noZoomConfig,
    tooltipContainerConfig,
    trendConfig,
} from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { ShareTooltip } from "app/components/ShareTooltip/ShareTooltip";
import PieChartTooltip from "app/pages/app-intelligence/app-analysis/components/GenericGraph/components/PieChartTooltip";
import { FORMATTERS } from "app/common/benchmark/formatters";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

import type { BenchmarkGranularity } from "../../../types/common";

export const defaultEmptyDataGraph = {
    data: [
        [1, 100],
        [2, 100],
        [3, 100],
        [4, 100],
        [5, 100],
        [6, 100],
    ],
};

export const defaultEmptyPieData = [
    {
        data: [
            {
                color: colorsPalettes.blue[200],
                id: "Default",
                name: "N/A",
                isEmpty: true,
                total: 100,
                y: 100,
            },
        ],
    },
];

export const gradient = {
    fillColor: "#3E74FE",
    stop1Color: "rgb(233, 241, 253)",
    stop2Color: "rgba(233, 241, 253, 0.2)",
};

export const getBarChart = (
    isConnected: boolean,
    granularity: BenchmarkGranularity,
    valueFormatter?: (val: number) => string,
) => {
    const graphParams = {
        type: chartTypes.AREA,
        ...gradient,
    };

    return combineConfigs(graphParams, [
        {},
        gradientConfig,
        trendConfig,
        noMarkerConfig,
        noZoomConfig,
        {
            chart: {
                margin: [0, 10, 20, 5],
                spacing: [0, 9, 0, 9],
                height: 64,
            },
            tooltip: {
                enabled: isConnected,
                outside: true,
                useHTML: true,
                shadow: false,
                backgroundColor: "transparent",
                formatter(this) {
                    return ReactDOMServer.renderToString(
                        <ShareTooltip
                            timestamp={this.name}
                            value={this.y}
                            granularity={granularity}
                            formatter={valueFormatter}
                        />,
                    );
                },
            },
        },
    ]);
};

export const getPieChartConfig = (isConnected: boolean) => ({
    chart: {
        height: 80,
        width: 80,
        animation: false,
        type: "pie",
        plotBackgroundColor: "transparent",
    },
    legend: {
        enabled: false,
    },
    tooltip: {
        enabled: isConnected,
        followPointer: true,
        outside: true,
        headerFormat: ``,
        useHTML: true,
        ...tooltipContainerConfig().tooltip,
        pointFormatter() {
            const value = FORMATTERS.numberAbbr(this.y);

            return ReactDOMServer.renderToString(
                <PieChartTooltip
                    name={this.name}
                    color={this.color}
                    value={this?.isEmpty ? "N/A" : value}
                />,
            );
        },
    },
    plotOptions: {
        pie: {
            innerSize: "60%",
            center: [10, 10],
            animation: false,
            showInLegend: true,
            cursor: "pointer",
            borderWidth: 0,
            borderRadius: 0,
            states: {
                hover: {
                    halo: null,
                },
            },
            dataLabels: {
                enabled: false,
            },
            size: 80,
        },
        series: {
            enableMouseTracking: isConnected,
        },
    },
});
